import { useState, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";

import DashboardLayout from "../../../Layout/DashboardLayout";
import { employeeID, companyID, sanitizeLabName, normalizeSpaces } from "../../../Common/UIComponents";
import {
  fetchSyncAvailable,
  fetchDriveConfig,
  saveDriveConfig,
} from "../../../API_Service/Settings_Slice";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const toArray = (res) => {
  const d = res?.payload?.data ?? res?.payload ?? res?.data ?? res;
  return Array.isArray(d) ? d : [];
};

const toBool = (res) => {
  const d = res?.payload?.data ?? res?.payload ?? res?.data ?? res;
  if (typeof d === "boolean") return d;
  if (typeof d === "string")  return d.toLowerCase() === "true";
  return !!d;
};

const FIELD_LABELS = {
  CLIENTID:           "Client ID",
  CLIENTSECRETVALUE:  "Client Secret Value",
  TENANTID:           "Tenant ID",
  SHAREPOINTSITENAME: "SharePoint Site Name",
  SHAREPOINTLIBRARY:  "SharePoint Library",
  CREDENTIALFILEPATH: "Credentials File",
  BUCKETNAME:         "Bucket Name",
};

const DRIVE_TYPE_HINTS = {
  ONEDRIVE:    "Ensure the Azure app has Sites.ReadWrite.All and Files.ReadWrite.All permissions.",
  GOOGLEDRIVE: "The service account must have access to the target Google Drive bucket.",
};

const PASSWORD_FIELDS  = ["CLIENTSECRETVALUE"];
const FILE_FIELDS      = ["CREDENTIALFILEPATH"];
const FIELD_MAX_LENGTH = 1000;

const CharCounter = ({ value, max }) => {
  const len = (value ?? "").length;
  if (len === 0) return null;
  const atLimit = len === max;
  return (
    <div className={`flex items-center gap-1 mt-1 text-xs ${atLimit ? "text-red-500 font-semibold" : "text-[#3e494a]/60"}`}>
      <span>{len}/{max}</span>
      {atLimit && <span className="text-red-500">— limit reached</span>}
    </div>
  );
};

// ─────────────────────────────────────────────
// FIELD LABEL
// ─────────────────────────────────────────────

const FieldLabel = ({ children, required }) => (
  <label className="block text-xs font-bold text-[#3e494a] uppercase tracking-widest ml-1 mb-2">
    {children}
    {required && <span className="text-[#ba1a1a] ml-1">*</span>}
  </label>
);

// ─────────────────────────────────────────────
// TEXT INPUT
// ─────────────────────────────────────────────

const TextInput = ({ type = "text", value, onChange, placeholder, error, disabled, maxLength }) => {
  const [showPwd, setShowPwd] = useState(false);
  const isPassword = type === "password";
  const inputType  = isPassword ? (showPwd ? "text" : "password") : type;

  return (
    <div>
      <div className={`flex items-center bg-[#e5e9e9] rounded-xl px-4 py-3 gap-2
        focus-within:ring-2 focus-within:ring-[#08757c] focus-within:bg-white transition-all
        ${error    ? "ring-2 ring-[#ba1a1a]" : ""}
        ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
        <input
          type={inputType}
          value={value}
          onChange={(e) => { const v = e.target.value.replace(/^\s+/, ""); onChange({ ...e, target: { ...e.target, value: v } }); }}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          className="flex-1 bg-transparent outline-none text-sm text-[#181c1d] placeholder:text-[#3e494a]/40"
        />
        {isPassword && (
          <button type="button" onClick={() => setShowPwd((p) => !p)}
            className="text-[#3e494a]/60 hover:text-[#08757c] transition-colors">
            <span className="material-symbols-outlined text-[18px]">
              {showPwd ? "visibility_off" : "visibility"}
            </span>
          </button>
        )}
      </div>
      {error && <p className="text-xs text-[#ba1a1a] mt-1 ml-1">{error}</p>}
    </div>
  );
};

// ─────────────────────────────────────────────
// FILE INPUT
// ─────────────────────────────────────────────

const FileInput = ({ value, onChange, existingFileName, error }) => (
  <div>
    <label className={`flex items-center gap-3 bg-[#e5e9e9] rounded-xl px-4 py-3 cursor-pointer
      hover:bg-[#d8dddd] transition-colors ${error ? "ring-2 ring-[#ba1a1a]" : ""}`}>
      <span className="material-symbols-outlined text-[#08757c] text-[20px]">upload_file</span>
      <div className="flex-1 min-w-0">
        {value ? (
          <p className="text-sm text-[#181c1d] font-medium truncate">{value.name}</p>
        ) : existingFileName ? (
          <p className="text-sm text-[#08757c] font-medium truncate">Selected: {existingFileName}</p>
        ) : (
          <p className="text-sm text-[#3e494a]/50">Upload .json credentials file</p>
        )}
        <p className="text-[10px] text-[#3e494a]/40 mt-0.5">Only .json files accepted</p>
      </div>
      <input type="file" accept=".json" onChange={onChange} className="hidden" />
    </label>
    {error && <p className="text-xs text-[#ba1a1a] mt-1 ml-1">{error}</p>}
  </div>
);

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────

export default function CredentialsConfig({ embedded = false }) {
  const dispatch = useDispatch();

  const [driveFields,   setDriveFields]   = useState([]);
  const [driveTypeCode, setDriveTypeCode] = useState("");
  const [syncAvailable, setSyncAvailable] = useState(false);
  const [form,          setForm]          = useState({});
  const [fileMap,       setFileMap]       = useState({});
  const [errors,        setErrors]        = useState({});
  const [isLoading,     setIsLoading]     = useState(true);
  const [isSaving,      setIsSaving]      = useState(false);
  const [saveSuccess,   setSaveSuccess]   = useState(false);
  const [saveError,     setSaveError]     = useState(null);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      dispatch(fetchSyncAvailable({ companyID })),
      dispatch(fetchDriveConfig({ companyID })),
    ]).then(([syncRes, configRes]) => {
      setSyncAvailable(toBool(syncRes));
      const fields = toArray(configRes);
      setDriveFields(fields);
      const type = fields[0]?.Shared_Folder_Type_Code ??
                   fields[0]?.SharedFolderTypeCode     ??
                   fields[0]?.shared_folder_type_code  ?? "";
      setDriveTypeCode(type);
      const prefilled = {};
      fields.forEach((f) => {
        const code = f.Shared_Folder_Field_Code ?? f.SharedFolderFieldCode ?? "";
        prefilled[code] = f.Shared_Folder_Field_Value ?? f.SharedFolderFieldValue ?? "";
      });
      setForm(prefilled);
    })
    .catch(() => {})
    .finally(() => setIsLoading(false));
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleField = useCallback((code) => (e) => {
    const raw = e?.target ? e.target.value : e;
    // Strip special characters for every text field except Client Secret Value
    // (passwords legitimately contain symbols) and file fields.
    const isPassword = PASSWORD_FIELDS.includes(code);
    const isFile     = FILE_FIELDS.includes(code);
    const value      = (typeof raw === "string" && !isPassword && !isFile)
      ? sanitizeLabName(raw)
      : raw;
    setForm((prev) => ({ ...prev, [code]: value }));
    setErrors((prev) => ({ ...prev, [code]: "" }));
    setSaveSuccess(false);
    setSaveError(null);
  }, []);

  const handleFile = useCallback((code) => (e) => {
    const file = e.target.files?.[0] ?? null;
    setFileMap((prev) => ({ ...prev, [code]: file }));
    setErrors((prev) => ({ ...prev, [code]: "" }));
    setSaveSuccess(false);
  }, []);

  const validate = () => {
    const errs = {};
    driveFields.forEach((f) => {
      const code   = f.Shared_Folder_Field_Code ?? f.SharedFolderFieldCode ?? "";
      const label  = FIELD_LABELS[code] ?? code;
      const isFile = FILE_FIELDS.includes(code);
      if (isFile) {
        const existingPath = f.Shared_Folder_Field_Value ?? f.SharedFolderFieldValue ?? "";
        if (!fileMap[code] && !existingPath) errs[code] = `${label} is required.`;
      } else {
        if (!String(form[code] ?? "").trim()) errs[code] = `${label} is required.`;
      }
    });
    return errs;
  };

  const handleSave = useCallback(async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    const driveConfigList = driveFields.map((f) => {
      const code       = f.Shared_Folder_Field_Code ?? f.SharedFolderFieldCode ?? "";
      const isFile     = FILE_FIELDS.includes(code);
      const isPassword = PASSWORD_FIELDS.includes(code);
      return {
        SharedFolderFieldValueId: f.Shared_Folder_Field_Value_Id ?? f.SharedFolderFieldValueId ?? 0,
        SharedFolderFieldId:      f.Shared_Folder_Field_Id       ?? f.SharedFolderFieldId       ?? 0,
        SharedFolderFieldCode:    code,
        SharedFolderTypeCode:     driveTypeCode,
        SharedFolderFieldValue:   isFile
          ? (f.Shared_Folder_Field_Value ?? f.SharedFolderFieldValue ?? "")
          : isPassword
          ? String(form[code] ?? "")
          : normalizeSpaces(String(form[code] ?? "")),
      };
    });
    const payload = { CompanyID: companyID, CreatedBy: employeeID, DriveConfigList: driveConfigList };
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    dispatch(saveDriveConfig(payload))
      .then((res) => {
        const result = res?.payload?.data ?? res?.payload;
        if (Number(result) > 0) {
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 4000);
          setForm((prev) =>
            Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, typeof v === "string" ? normalizeSpaces(v) : v]))
          );
        }
        else setSaveError("Failed to save. Please try again.");
      })
      .catch((err) => setSaveError(err?.message ?? "Failed to save configuration."))
      .finally(() => setIsSaving(false));
  }, [form, fileMap, driveFields, driveTypeCode, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const driveHint = DRIVE_TYPE_HINTS[driveTypeCode] ?? "";

  const SaveButton = () => (
    <button onClick={handleSave} disabled={isSaving}
      className="px-8 py-2.5 bg-gradient-to-br from-[#08757c] to-[#005b60] text-white text-sm font-bold rounded-xl shadow-lg shadow-[#08757c]/20 hover:opacity-90 active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
      {isSaving
        ? <><span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>Saving…</>
        : <><span className="material-symbols-outlined text-[18px]">save</span>Save Configuration</>}
    </button>
  );

  const content = (
    <div className={embedded ? "text-[#181c1d]" : "px-8 py-8 bg-[#f7fafa] min-h-screen text-[#181c1d]"}>

      {/* Breadcrumb — only when standalone */}
      {!embedded && (
        <nav className="flex items-center gap-2 text-xs text-[#3e494a] mb-2">
          <span>App Configuration</span>
          <span className="material-symbols-outlined text-[14px]">chevron_right</span>
          <span className="text-[#08757C] font-semibold">Credentials</span>
        </nav>
      )}

      {/* Heading — only when standalone */}
      {!embedded && (
        <div className="flex flex-wrap justify-between items-end mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-[#181c1d]">Credentials</h1>
            <p className="text-[#3e494a] text-sm mt-1">
              Configure your cloud storage credentials for file sync and document management.
            </p>
          </div>
          {!isLoading && driveFields.length > 0 && <SaveButton />}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center h-48 text-[#3e494a] text-sm gap-3">
          <span className="material-symbols-outlined animate-spin text-[#08757c]">progress_activity</span>
          Loading configuration…
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-8">

          {/* LEFT */}
          <div className="col-span-12 lg:col-span-4 space-y-6">
            <div className="bg-white p-6 rounded-2xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/40">
              <h4 className="text-xs font-bold uppercase tracking-widest text-[#3e494a] mb-4">Required Fields</h4>
              <div className="space-y-2">
                {driveFields.map((f) => {
                  const code     = f.Shared_Folder_Field_Code ?? f.SharedFolderFieldCode ?? "";
                  const label    = FIELD_LABELS[code] ?? code;
                  const isFilled = FILE_FIELDS.includes(code)
                    ? !!(fileMap[code] || f.Shared_Folder_Field_Value || f.SharedFolderFieldValue)
                    : !!(form[code] ?? "").trim();
                  return (
                    <div key={code} className="flex items-center justify-between">
                      <p className="text-xs text-[#3e494a]">{label}</p>
                      <span className={`material-symbols-outlined text-[16px] ${isFilled ? "text-[#436900]" : "text-[#bdc9c9]"}`}>
                        {isFilled ? "check_circle" : "radio_button_unchecked"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {driveHint && (
              <div className="bg-[#f1f4f4] p-4 rounded-xl flex gap-3 items-start border border-[#bdc9c9]/10">
                <span className="material-symbols-outlined text-[#08757c] text-[18px] mt-0.5">info</span>
                <p className="text-xs text-[#3e494a] leading-relaxed">{driveHint}</p>
              </div>
            )}
          </div>

          {/* RIGHT */}
          <div className="col-span-12 lg:col-span-8 space-y-6">
            {driveFields.length === 0 ? (
              <div className="bg-white p-8 rounded-2xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/40">
                <p className="text-sm text-[#3e494a]">No configuration fields found.</p>
              </div>
            ) : (
              <div className="bg-white p-8 rounded-2xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/40">
                <h3 className="text-base font-bold flex items-center gap-2 mb-6">
                  <span className="material-symbols-outlined text-[#08757c] text-[20px]">key</span>
                  Credentials Settings
                </h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-5">
                  {driveFields.map((f) => {
                    const code       = f.Shared_Folder_Field_Code ?? f.SharedFolderFieldCode ?? "";
                    const label      = FIELD_LABELS[code] ?? code;
                    const isFile     = FILE_FIELDS.includes(code);
                    const isPassword = PASSWORD_FIELDS.includes(code);

                    if (isFile) return (
                      <div key={code} className="col-span-2">
                        <FieldLabel required>{label}</FieldLabel>
                        <FileInput value={fileMap[code] ?? null} onChange={handleFile(code)}
                          existingFileName={(f.Shared_Folder_Field_Value ?? f.SharedFolderFieldValue ?? "").split(/[\\/]/).pop() || null}
                          error={errors[code]} />
                      </div>
                    );

                    return (
                      <div key={code}>
                        <FieldLabel required>{label}</FieldLabel>
                        <TextInput type={isPassword ? "password" : "text"} value={form[code] ?? ""}
                          onChange={handleField(code)} placeholder={`Enter ${label}`} error={errors[code]} maxLength={FIELD_MAX_LENGTH} />
                        <CharCounter value={form[code] ?? ""} max={FIELD_MAX_LENGTH} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {saveSuccess && (
              <div className="bg-[#bbf46e]/20 text-[#436900] rounded-xl px-5 py-4 text-sm flex items-center gap-3 border border-[#bbf46e]/40">
                <span className="material-symbols-outlined text-[20px]">check_circle</span>
                <span className="font-semibold">Credentials saved successfully.</span>
              </div>
            )}
            {saveError && (
              <div className="bg-red-50 text-[#ba1a1a] rounded-xl px-4 py-3 text-sm flex items-center gap-3">
                <span className="material-symbols-outlined text-[20px]">error</span>
                {saveError}
              </div>
            )}

            {driveFields.length > 0 && (
              <div className="flex justify-end">
                <SaveButton />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  if (embedded) return content;

  return <DashboardLayout>{content}</DashboardLayout>;
}
